import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import determineNextLoyaltyTier from 'loyalty/lib/determineNextLoyaltyTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { isSnackbarShown, markSnackbarAsShown } from 'loyalty/storage/tierProgressSnackbar'
import { useAppSelector } from 'hooks/reduxHooks'

interface Params {
  memberId?: string
}

function useLoyaltyTierProgressSnackbar(params: Params) {
  const { memberId } = params
  const intl = useIntl()

  const loyaltyAccountState = useAppSelector(state => state.loyalty?.account)

  const isInitial = loyaltyAccountState?.initial
  const isFetching = loyaltyAccountState?.fetching
  const loyaltyAccount = loyaltyAccountState?.data

  const viewProgressLabel = intl.formatMessage({
    id: 'FHdh8OpTNH',
    defaultMessage: 'View progress',
    description: 'Action label on snackbar linking to loyalty tier progress page',
  })

  const viewProgressAction = useMemo(() => ({
    label: viewProgressLabel,
    to: `/${LOYALTY_LANDING_PAGE}#benefits`,
  }), [viewProgressLabel])

  const snackbarMessage = useMemo(() => {
    if (isInitial || isFetching) return undefined
    if (!loyaltyAccount) return undefined

    const nextTier = determineNextLoyaltyTier(loyaltyAccount.tier)
    if (!nextTier) return undefined

    const statusCredits = loyaltyAccount.statusCredits ?? 0
    const totalCreditsForTierUpgrade = loyaltyAccount.totalCreditsForTierUpgrade ?? 0
    const numberOfNights = loyaltyAccount.numberOfNights ?? 0
    const numberOfNightsForTierUpgrade = loyaltyAccount.numberOfNightsForTierUpgrade ?? 0

    const creditsRemaining = Math.ceil(totalCreditsForTierUpgrade - statusCredits)
    const nightsRemaining = Math.ceil(numberOfNightsForTierUpgrade - numberOfNights)

    if (creditsRemaining > 50) return undefined
    if (creditsRemaining <= 0 && nightsRemaining <= 0) return undefined

    const nextTierName = getLoyaltyTierDisplayName(nextTier)
    if (!nextTierName) return undefined

    const requirements: Array<string> = []

    if (creditsRemaining > 0) {
      requirements.push(intl.formatMessage(
        {
          id: '25RFRoCaI6',
          defaultMessage: '{creditsRemaining, plural, one {# more status credit} other {# more status credits}}',
          description: 'Remaining status credits needed for next loyalty tier upgrade',
        },
        { creditsRemaining },
      ))
    }

    if (nightsRemaining > 0) {
      requirements.push(intl.formatMessage(
        {
          id: 'U/J39WLrxb',
          defaultMessage: '{nightsRemaining, plural, one {# more night} other {# more nights}}',
          description: 'Remaining nights needed for next loyalty tier upgrade',
        },
        { nightsRemaining },
      ))
    }

    const joinedRequirements = requirements.join(intl.formatMessage({
      id: 'D7IPqEN1+A',
      defaultMessage: ' and ',
      description: 'Conjunction joining credit and night requirements in tier progress snackbar',
    }))

    return intl.formatMessage(
      {
        id: 'aOW4b1BKTi',
        defaultMessage: 'You\u2019re so close to {nextTierName} Société status! Only {requirements} to go.',
        description: 'Snackbar message when user is close to next loyalty tier, showing remaining requirements',
      },
      { nextTierName, requirements: joinedRequirements },
    )
  }, [intl, isFetching, isInitial, loyaltyAccount])

  useEffect(() => {
    if (!memberId) return
    if (!snackbarMessage) return

    if (isSnackbarShown(memberId)) {
      return
    }

    showSnackbar(snackbarMessage, 'success', { action: viewProgressAction })
    markSnackbarAsShown(memberId)
  }, [memberId, snackbarMessage, viewProgressAction])
}

export default useLoyaltyTierProgressSnackbar
